<template>
  <v-container v-if="$can('browse', 'Checklist')">
    <div
      class="headline mb-6"
    >
      Checklists
    </div>

    <v-row>
      <!--<editor-fold desc="Filters column">-->
      <v-col
        sm="3"
        class="hidden-xs-only"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <yacht-modal-select
            v-model="filtersDirty.yacht_ids"
            :dialog-visible.sync="yachtModalSelectVisible"
            label="Yachts"
            solo
            hide-details
            clearable
            multiple
            chips
            class="mb-2"
            @submit="yachtModalSelectVisible = false"
          />
          <v-subheader>
            Check in
          </v-subheader>
          <v-row
            dense
            class="mb-2"
          >
            <v-col
              cols="8"
            >
              <v-text-field
                ref="check_in_from"
                :value="filtersDirty.check_in_from ? $moment(filtersDirty.check_in_from).format('DD.MM.YYYY.') : ''"
                solo
                hide-details
                label="From"
                readonly
                clearable
                @click:clear="filtersDirty.check_in_from = null"
                @click="showDatePickerFor('check_in_from', null, 'check_in_to')"
              />
            </v-col>
            <v-col cols="8">
              <v-text-field
                ref="check_in_to"
                :value="filtersDirty.check_in_to ? $moment(filtersDirty.check_in_to).format('DD.MM.YYYY.') : ''"
                solo
                hide-details
                label="To"
                readonly
                clearable
                @click:clear="filtersDirty.check_in_to = null"
                @click="showDatePickerFor('check_in_to', 'check_in_from')"
              />
            </v-col>
          </v-row>
          <v-subheader>
            Check out
          </v-subheader>
          <v-row
            dense
            class="mb-2"
          >
            <v-col
              cols="8"
            >
              <v-text-field
                ref="check_out_from"
                :value="filtersDirty.check_out_from ? $moment(filtersDirty.check_out_from).format('DD.MM.YYYY.') : ''"
                solo
                hide-details
                label="From"
                readonly
                clearable
                @click:clear="filtersDirty.check_out_from = null"
                @click="showDatePickerFor('check_out_from', null, 'check_out_to')"
              />
            </v-col>
            <v-col cols="8">
              <v-text-field
                ref="check_out_to"
                :value="filtersDirty.check_out_to ? $moment(filtersDirty.check_out_to).format('DD.MM.YYYY.') : ''"
                solo
                hide-details
                label="To"
                readonly
                clearable
                @click:clear="filtersDirty.check_out_to = null"
                @click="showDatePickerFor('check_out_to', 'check_out_from')"
              />
            </v-col>
          </v-row>
        </div>
        <v-menu
          v-model="datePicker.show"
          :close-on-content-click="false"
          transition="fade-transition"
          min-width="290px"
          absolute
          :position-x="datePicker.position.x"
          :position-y="datePicker.position.y"
        >
          <v-date-picker
            v-model="datePicker.date"
            no-title
            :min="datePicker.minDate"
            :max="datePicker.maxDate"
          />
        </v-menu>
      </v-col>
      <!--</editor-fold>-->
      <!--<editor-fold desc="Data table column">-->
      <v-col
        sm="13"
        cols="16"
      >
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="checklists.meta.from"
          :to="checklists.meta.to"
          :total="checklists.meta.total"
          :last-page="checklists.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->

        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="tableHeaders"
          :items="checklists.data"
          :loading="checklists.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="checklists.meta.total"
          must-sort
          hide-default-footer
          class="elevation-5"
        >
          <template v-slot:item="{ item: checklist }">
            <checklist-row
              :checklist="checklist"
              @click.native="openChecklistViewDialog(checklist)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <div
              v-if="isFiltered"
              class="text-center py-4"
            >
              <div>
                There are no results for your search.
              </div>
              <div
                class="mt-2"
              >
                <v-btn
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </div>
            </div>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <div
              v-else
              class="text-center py-4"
            >
              There are no checklists at the moment.
            </div>
            <!--</editor-fold>-->
          </template>
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
      <!--</editor-fold>-->
    </v-row>

    <checklist-view-dialog
      v-if="checklistViewDialog"
      :checklist="checklistDirty"
      :visible.sync="checklistViewDialog"
    />
  </v-container>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import paginationMixin from '@/mixins/paginationMixin'
  import filterableMixin from '@/mixins/filterableMixin'
  import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import Checklist from '@/models/Checklist'
  import ChecklistRow from '@/components/service/checklists/ChecklistRow'
  import ChecklistViewDialog from '@/components/service/checklists/ChecklistViewDialog'
  import YachtModalSelect from '@/components/service/helpers/YachtModalSelect'

  export default {
    name: 'ChecklistIndex',
    components: {
      YachtModalSelect,
      ChecklistRow,
      ChecklistViewDialog
    },
    mixins: [paginationMixin, filterableMixin, serviceCurrentLocationMixin, deleteConfirmationMixin],
    data() {
      return {
        checklists: {
          data: [],
          meta: {},
          loading: false
        },
        filtersDirty: cloneDeep(Checklist.emptyFilters),
        filtersDialog: false,
        datePicker: {
          show: false,
          date: null,
          ref: null,
          minDate: null,
          maxDate: null,
          position: {
            x: 0,
            y: 0
          }
        },
        checklistDirty: null,
        checklistViewDialog: false,
        yachtModalSelectVisible: false
      }
    },
    computed: {
      tableHeaders() {
        return [
          { text: 'Yacht', value: 'yacht.name', sortable: false, width: '1%' },
          { text: 'Guest', value: 'yacht.check_in.guest_name', sortable: false, width: '1%' },
          { text: 'Check In', value: 'yacht.check_in.created_at', sortable: true },
          { text: 'Check Out', value: 'yacht.check_out.created_at', sortable: true }
        ]
      }
    },
    watch: {
      'datePicker.date': function (value) {
        if (!value) return

        this.filtersDirty[this.datePicker.ref] = value

        this.datePicker.show = false
      }
    },
    created() {
      this.fetchChecklists()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchChecklists()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      onCurrentLocationUpdate() {
        this.resetPagination()
      },
      async fetchChecklists() {
        this.checklists.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          ...this.currentLocationQueryParams
        }

        if (queryParams.yacht_ids && queryParams.yacht_ids.length > 0) queryParams.yacht_ids = queryParams.yacht_ids.map(y => y.id)

        try {
          let result = await Checklist.api.fetch(queryParams)

          this.checklists.data = result.data
          this.checklists.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.checklists.loading = false
        }
      },
      showDatePickerFor(ref, min, max) {
        this.datePicker.ref = ref
        this.datePicker.date = this.filtersDirty[ref]
        this.datePicker.minDate = min && this.filtersDirty[min] ? this.filtersDirty[min] : null
        this.datePicker.maxDate = max && this.filtersDirty[max] ? this.filtersDirty[max] : this.$moment().format('YYYY-MM-DD')

        let rect = this.$refs[ref].$el.getBoundingClientRect()

        this.datePicker.position.x = rect.left
        this.datePicker.position.y = rect.top + rect.height

        this.$nextTick(() => {
          this.datePicker.show = true
        })
      },
      openChecklistViewDialog(checklist) {
        this.checklistDirty = checklist
        this.checklistViewDialog = true
      }
    }
  }
</script>
